import React, { useState, useEffect } from "react";
import api from "../../utils/api";
import useCurrentUser from "../../hooks/useCurrentUser";
import { Package, Send, Truck, CheckCircle, Clock } from "lucide-react";
import { useNavigate } from "react-router-dom";

export default function VendorDashboard() {
  const { userName } = useCurrentUser();
  const navigate = useNavigate();
  const [stats, setStats] = useState({ total: 0, draft: 0, submitted: 0, in_transit: 0, completed: 0 });
  const [recent, setRecent] = useState([]);

  useEffect(() => {
    api.get("/api/inbound").then(res => {
      const shipments = res.data.shipments || [];
      setStats({
        total:      shipments.length,
        draft:      shipments.filter(s => s.status === "draft").length,
        submitted:  shipments.filter(s => s.status === "submitted").length,
        in_transit: shipments.filter(s => s.status === "in_transit").length,
        completed:  shipments.filter(s => s.status === "completed").length,
      });
      setRecent(shipments.slice(0, 5));
    }).catch(() => {});
  }, []);

  const STATUS_CFG = {
    draft:      { cls: "bg-slate-100 text-slate-500", label: "Draft" },
    submitted:  { cls: "bg-blue-100 text-blue-700", label: "Submitted" },
    approved:   { cls: "bg-emerald-100 text-emerald-700", label: "Approved" },
    in_transit: { cls: "bg-amber-100 text-amber-700", label: "In Transit" },
    arrived:    { cls: "bg-violet-100 text-violet-700", label: "Arrived" },
    validated:  { cls: "bg-teal-100 text-teal-700", label: "Validated" },
    completed:  { cls: "bg-green-100 text-green-700", label: "Completed" },
  };

  return (
    <div className="max-w-5xl mx-auto py-8 px-4">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-navy-900">Welcome, {userName}</h1>
        <p className="text-slate-500 text-sm mt-1">Vendor Portal · Create and track your inbound shipments</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        {[
          { label: "Total ASNs",   value: stats.total,      icon: <Package size={18} className="text-violet-500"/>  },
          { label: "Drafts",       value: stats.draft,      icon: <Clock size={18} className="text-slate-400"/>     },
          { label: "In Transit",   value: stats.in_transit, icon: <Truck size={18} className="text-amber-500"/>     },
          { label: "Completed",    value: stats.completed,  icon: <CheckCircle size={18} className="text-green-500"/>},
        ].map((m, i) => (
          <div key={i} className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{m.label}</span>
              {m.icon}
            </div>
            <p className="text-2xl font-bold text-navy-900">{m.value}</p>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-navy-900">Recent ASNs</h3>
          <button onClick={() => navigate("/vendor/inbound")}
            className="text-xs text-violet-600 font-bold hover:text-violet-800">View All →</button>
        </div>
        {recent.length === 0 ? (
          <p className="text-sm text-slate-400 text-center py-6">No shipments yet.</p>
        ) : (
          <div className="space-y-3">
            {recent.map(s => {
              const cfg = STATUS_CFG[s.status] || STATUS_CFG.draft;
              return (
                <div key={s.id} className="flex items-center justify-between py-2 border-b border-slate-50 last:border-0">
                  <div>
                    <p className="font-semibold text-navy-900 font-mono text-sm">{s.asnNumber}</p>
                    <p className="text-xs text-slate-400">To: {s.companyId} · {s.items?.length || 0} items</p>
                  </div>
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${cfg.cls}`}>{cfg.label}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <button onClick={() => navigate("/vendor/inbound")}
        className="w-full py-4 bg-navy-900 text-white rounded-2xl font-bold flex items-center justify-center gap-2 hover:bg-navy-800 transition-colors">
        <Send size={18}/> Create New ASN
      </button>
    </div>
  );
}
